/**
 * UnauthorizedPage - Shown when user lacks permission to access a route
 *
 * Simple, clean page with:
 * - Lock icon
 * - "Unauthorized" title
 * - Helpful message
 * - "Go Back" button
 */

import {ShieldX} from 'lucide-react'
import React, {useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import styled from 'styled-components'

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  background: #fafafa;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #fff3f3;
  color: #e53935;
  margin-bottom: 24px;
`

const Title = styled.h1`
  font-family: 'Inter', sans-serif;
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 12px 0;
  text-align: center;
`

const Message = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-size: 1rem;
  color: #666;
  margin: 0 0 32px 0;
  text-align: center;
  max-width: 400px;
  line-height: 1.5;
`

const BackButton = styled.button`
  padding: 12px 32px;
  background: #03a9fc;
  color: white;
  border: none;
  border-radius: 6px;
  font-family: 'Open Sans', sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #0288d1;
  }
`

const HomeLink = styled.button`
  margin-top: 16px;
  padding: 8px 16px;
  background: transparent;
  color: #666;
  border: none;
  font-family: 'Open Sans', sans-serif;
  font-size: 0.875rem;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #03a9fc;
  }
`

// ============================================================================
// COMPONENT
// ============================================================================

export const UnauthorizedPage: React.FC = () => {
  const navigate = useNavigate()

  const handleGoBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const handleGoHome = useCallback(() => {
    navigate('/dashboard')
  }, [navigate])

  return (
    <Container>
      <IconWrapper>
        <ShieldX size={40} />
      </IconWrapper>
      <Title>Unauthorized</Title>
      <Message>
        You don&apos;t have permission to access this page. Please contact your administrator if you
        believe this is an error.
      </Message>
      <BackButton onClick={handleGoBack}>Go Back</BackButton>
      <HomeLink onClick={handleGoHome}>Return to Dashboard</HomeLink>
    </Container>
  )
}

export default UnauthorizedPage
